import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { getRawCategoriesForRef, businessGroups } from "../utils/spendingCategories";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

export const budgetConfig = {
	startingDate: "2026-01-01",
	historicalCutoff: "2026-01",
	balanceAccounts: ["NL: Cash", "NL: Checking - D100", "Erin: PNC - Checking", "NL: Immerge Checking", "Erin: PNC - Savings", "NL: Immerge Saving", "NL: Home Saving - S000", "NL: Chase - Sapphire Preferred", "NL: Chase - United Explorer", "NL: Chase - Freedom", "NL: Chase - Ink Bold Preferred", "NL: Chase - United Business", "Erin: Chase - Sapphire Preferred", "Erin: PNC - Points Credit Card", "Erin: PNC - CashBack Credit Card", "NL: Home - CL", "NL: COIN", "NL: VAN"],
};

export const businessExpensesDefault = 3420;
export const taxRate = 0.35;
export const monthlyGrossEstimate = 12500;

export const calculateInvoiceTotal = (invoice) => {
	const rate = parseFloat(invoice.client_rate) || 125;
	const hours = parseFloat(invoice.calculated_hours) || 0;
	const flatFees = parseFloat(invoice.flat_fees_total) || 0;
	return hours * rate + flatFees;
};

/**
 * useBudgetForecast - Shared hook for budget forecast data and calculations.
 *
 * Extracts all data fetching, balance calculation, forecast processing,
 * and derived values from BudgetForecast.jsx so they can be reused
 * (e.g. by MoneyHome for the EOM Estimate stat card).
 */
const useBudgetForecast = ({
	years = 1,
	showPreviousYear = false,
	estimatedMonthlyGross = monthlyGrossEstimate,
	variableItems = new Set(),
	variableValues = {},
} = {}) => {
	// Data state
	const [forecastData, setForecastData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [calculatedBalance, setCalculatedBalance] = useState(null);
	const [invoiceData, setInvoiceData] = useState([]);
	const [lineItems, setLineItems] = useState([]);
	const [historicalIncome, setHistoricalIncome] = useState([]);
	const [networthEntries, setNetworthEntries] = useState([]);

	// Fetch initial data
	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				const [balancesRes, invoicesRes, lineItemsRes, incomeRes, networthRes] = await Promise.all([axios.get(`${backendUrl}/money/AccountBalance/`), axios.get(`${backendUrl}/invoice/invoices`), axios.get(`${backendUrl}/money/lineItems`), axios.get(`${backendUrl}/business/income`), axios.get(`${backendUrl}/networth/entries`)]);

				const balances = balancesRes.data;
				const accountTotals = {};
				balances.forEach((entry) => {
					if (budgetConfig.balanceAccounts.includes(entry.account)) {
						if (!accountTotals[entry.account] || new Date(entry.date) > new Date(accountTotals[entry.account].date)) {
							accountTotals[entry.account] = entry;
						}
					}
				});
				const total = Object.values(accountTotals).reduce((sum, acc) => sum + (acc.value || 0), 0);

				// Pull latest tax estimate from account balances
				const taxEntry = balances
					.filter((e) => e.account === "NL: Tax Estimate")
					.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
				const taxEstimate = taxEntry ? Math.abs(taxEntry.value) : 0;

				setCalculatedBalance(Math.round(total - taxEstimate));
				setInvoiceData(invoicesRes.data);
				setLineItems(lineItemsRes.data);
				setHistoricalIncome(incomeRes.data);
				setNetworthEntries(networthRes.data);
			} catch (err) {
				console.error("Error fetching initial data:", err);
				setCalculatedBalance(0);
			}
		};
		fetchInitialData();
	}, []);

	// Fetch forecast data
	useEffect(() => {
		if (calculatedBalance === null) return;

		const fetchForecast = async () => {
			setLoading(true);
			try {
				const res = await axios.get(`${backendUrl}/budget/forecast`, {
					params: {
						years,
						startingBalance: calculatedBalance,
						startDate: budgetConfig.startingDate,
						showPreviousYear,
					},
				});
				setForecastData(res.data);
				setError(null);
			} catch (err) {
				console.error("Error fetching forecast:", err);
				setError("Failed to load forecast data");
			} finally {
				setLoading(false);
			}
		};
		fetchForecast();
	}, [years, calculatedBalance, showPreviousYear]);

	// Effective item value (with variable overrides)
	const getEffectiveItemValue = useCallback(
		(item, dateKey) => {
			const originalValue = item.monthlyValues[dateKey] || 0;
			if (variableItems.has(item.id) && variableValues[item.id] !== undefined && originalValue !== 0) {
				return variableValues[item.id];
			}
			return originalValue;
		},
		[variableItems, variableValues],
	);

	// Group items by section and category
	const groupedItems = useMemo(() => {
		if (!forecastData?.items) return {};

		const groups = {};
		forecastData.items.forEach((item) => {
			const section = item.section || "Other";
			const category = item.category || "General";

			if (!groups[section]) {
				groups[section] = { categories: {}, isIncome: item.is_income };
			}
			if (!groups[section].categories[category]) {
				groups[section].categories[category] = [];
			}
			groups[section].categories[category].push(item);
		});

		return groups;
	}, [forecastData]);

	// Process invoice data into monthly totals
	const invoicesByMonth = useMemo(() => {
		if (!forecastData?.months) return {};

		const byMonth = {};
		forecastData.months.forEach((m) => {
			byMonth[m.dateKey] = { paid: 0, sent: 0, open: 0, total: 0 };
		});

		invoiceData
			.filter((inv) => ["paid", "sent", "open"].includes(inv.status) && inv.billing_year && inv.billing_month)
			.forEach((inv) => {
				const dateKey = `${inv.billing_year}-${String(inv.billing_month).padStart(2, "0")}`;
				if (byMonth[dateKey]) {
					const amount = calculateInvoiceTotal(inv);
					byMonth[dateKey][inv.status] += amount;
					byMonth[dateKey].total += amount;
				}
			});

		return byMonth;
	}, [invoiceData, forecastData]);

	// Current date key for comparisons
	const currentDateKey = useMemo(() => {
		const now = new Date();
		return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
	}, []);

	// Overdue amount: sum of sent + open from all closed months (at/after historicalCutoff)
	const overdueAmount = useMemo(() => {
		let total = 0;
		Object.entries(invoicesByMonth).forEach(([dateKey, data]) => {
			if (dateKey < currentDateKey && dateKey >= budgetConfig.historicalCutoff) {
				total += data.sent + data.open;
			}
		});
		return total;
	}, [invoicesByMonth, currentDateKey]);

	// Process historical income into monthly totals
	const historicalIncomeByMonth = useMemo(() => {
		const byMonth = {};
		historicalIncome.forEach((record) => {
			const dateKey = `${record.year}-${String(record.month).padStart(2, "0")}`;
			byMonth[dateKey] = (byMonth[dateKey] || 0) + (record.value || 0);
		});
		return byMonth;
	}, [historicalIncome]);

	// Process net worth entries to get last entry per month
	const networthByMonth = useMemo(() => {
		const byMonth = {};
		networthEntries.forEach((entry) => {
			const date = new Date(entry.date);
			const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
			if (!byMonth[dateKey]) {
				byMonth[dateKey] = entry.value;
			}
		});
		return byMonth;
	}, [networthEntries]);

	// Process actual spending by business for closed months
	const actualSpendingByMonth = useMemo(() => {
		const byMonth = {
			Home: {},
			Apartment: {},
			Other: {},
			ErinIncome: {},
		};

		const otherCategories = new Set();
		(businessGroups.Other || []).forEach((group) => {
			group.categories.forEach((cat) => otherCategories.add(cat.toLowerCase()));
		});

		lineItems.forEach((item) => {
			const date = new Date(item.date);
			const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
			const business = item.business || "";
			const category = (item.category || "").toLowerCase();

			if (business === "Home" && item.category === "Erin Paycheck") {
				byMonth.ErinIncome[dateKey] = (byMonth.ErinIncome[dateKey] || 0) + (item.transaction || 0);
				return;
			}

			if (otherCategories.has(category)) {
				if (!byMonth.Other[dateKey]) byMonth.Other[dateKey] = 0;
				byMonth.Other[dateKey] += item.transaction || 0;
				return;
			}

			if (business === "Home" || business === "Apartment") {
				if (!byMonth[business][dateKey]) {
					byMonth[business][dateKey] = 0;
				}
				byMonth[business][dateKey] += item.transaction || 0;
			}
		});

		["Home", "Apartment", "Other"].forEach((biz) => {
			Object.keys(byMonth[biz]).forEach((key) => {
				byMonth[biz][key] = Math.abs(byMonth[biz][key]);
			});
		});

		Object.keys(byMonth.ErinIncome).forEach((key) => {
			byMonth.ErinIncome[key] = Math.abs(byMonth.ErinIncome[key]);
		});

		return byMonth;
	}, [lineItems]);

	// Calculate actual amounts for large-ticket items by month
	const liveActualsByItem = useMemo(() => {
		if (!forecastData?.items || !lineItems.length) return {};

		const actuals = {};
		const largeTicketItems = forecastData.items.filter((item) => item.is_large_ticket && item.live_category_ref);

		largeTicketItems.forEach((item) => {
			let refs = item.live_category_ref;
			if (typeof refs === "string") {
				try {
					refs = JSON.parse(refs);
				} catch {
					refs = [];
				}
			}
			if (!refs.length) return;

			const matchMap = {};
			refs.forEach((ref) => {
				const [business, category] = ref.split(":");
				if (!business || !category) return;
				const businessLower = business.toLowerCase();
				if (!matchMap[businessLower]) matchMap[businessLower] = new Set();
				const rawCats = getRawCategoriesForRef(ref);
				rawCats.forEach((cat) => matchMap[businessLower].add(cat.toLowerCase()));
			});

			const paymentsByMonth = {};
			lineItems.forEach((lineItem) => {
				const itemBusiness = (lineItem.business || "").toLowerCase();
				const itemCategory = (lineItem.category || "").toLowerCase();
				const businessCategories = matchMap[itemBusiness];
				if (!businessCategories || !businessCategories.has(itemCategory)) return;

				const date = new Date(lineItem.date);
				const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
				paymentsByMonth[dateKey] = (paymentsByMonth[dateKey] || 0) + Math.abs(lineItem.transaction || 0);
			});

			if (Object.keys(paymentsByMonth).length > 0) {
				actuals[item.id] = paymentsByMonth;
			}
		});

		return actuals;
	}, [forecastData, lineItems]);

	// Helper to get actual amount for a large-ticket item
	const getLiveItemData = useCallback(
		(itemId, dateKey, forecastAmount) => {
			const itemActuals = liveActualsByItem[itemId];
			if (!itemActuals) {
				return { hasData: false, actual: 0, remaining: forecastAmount };
			}
			const actual = itemActuals[dateKey] || 0;
			const budget = Math.abs(forecastAmount);
			const remaining = Math.max(0, budget - actual);
			return { hasData: true, actual, remaining };
		},
		[liveActualsByItem],
	);

	// Month status helpers
	const isMonthClosed = useCallback((dateKey) => {
		const now = new Date();
		const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
		return dateKey < currentKey;
	}, []);

	const isCurrentMonth = useCallback((dateKey) => {
		const now = new Date();
		const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
		return dateKey === currentKey;
	}, []);

	const shouldShowActuals = useCallback(
		(dateKey) => {
			return isMonthClosed(dateKey) || isCurrentMonth(dateKey);
		},
		[isMonthClosed, isCurrentMonth],
	);

	// Historical expenses by month
	const historicalExpensesByMonth = useMemo(() => {
		const byMonth = {};
		const projectNames = businessGroups.Projects?.flatMap((g) => g.categories) || [];
		const businessNames = ["NL", "Immerge", ...projectNames];

		const startDate = new Date(budgetConfig.startingDate);
		const cutoffDate = new Date(budgetConfig.historicalCutoff + "-01");

		const matchingItems = lineItems.filter((item) => {
			if (!businessNames.includes(item.business)) return false;
			const itemDate = new Date(item.date);
			return itemDate >= startDate && itemDate < cutoffDate;
		});

		matchingItems.forEach((item) => {
			const date = new Date(item.date);
			const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
			byMonth[dateKey] = (byMonth[dateKey] || 0) + (item.transaction || 0);
		});

		Object.keys(byMonth).forEach((key) => {
			byMonth[key] = Math.abs(byMonth[key]);
		});

		return byMonth;
	}, [lineItems]);

	// Month progress helpers
	const getMonthProgress = useCallback(() => {
		const now = new Date();
		const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
		return now.getDate() / daysInMonth;
	}, []);

	const getCurrentMonthBounds = useCallback(() => {
		const now = new Date();
		const start = new Date(now.getFullYear(), now.getMonth(), 1);
		const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
		return { start, end };
	}, []);

	// Invoice helpers
	const getEffectiveGross = useCallback(
		(dateKey) => {
			const invData = invoicesByMonth[dateKey] || { paid: 0, sent: 0, open: 0, total: 0 };

			if (isCurrentMonth(dateKey)) {
				const unpaid = invData.sent + invData.open + overdueAmount;
				return unpaid > 0 ? unpaid : estimatedMonthlyGross;
			}

			return invData.total > 0 ? invData.total : estimatedMonthlyGross;
		},
		[invoicesByMonth, isCurrentMonth, estimatedMonthlyGross, overdueAmount],
	);

	const isUsingEstimate = useCallback(
		(dateKey) => {
			const invData = invoicesByMonth[dateKey] || { paid: 0, sent: 0, open: 0, total: 0 };

			if (isCurrentMonth(dateKey)) {
				return (invData.sent + invData.open + overdueAmount) === 0;
			}

			return invData.total === 0;
		},
		[invoicesByMonth, isCurrentMonth, overdueAmount],
	);

	// Get business expenses for a month
	const getBusinessExpenses = useCallback(
		(dateKey) => {
			const businessExpenseItems = (forecastData?.items || []).filter((item) => item.section === "Business" && !item.is_income);

			if (businessExpenseItems.length === 0) {
				return businessExpensesDefault;
			}

			let total = businessExpenseItems.reduce((sum, item) => {
				const forecastValue = getEffectiveItemValue(item, dateKey);
				if (item.is_large_ticket) {
					const liveData = getLiveItemData(item.id, dateKey, forecastValue);
					return sum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
				}
				return sum + forecastValue;
			}, 0);

			if (isCurrentMonth(dateKey)) {
				total = total * (1 - getMonthProgress());
			}

			return Math.abs(total);
		},
		[forecastData, getEffectiveItemValue, getLiveItemData, isCurrentMonth, getMonthProgress],
	);

	// Actual spending helper for current month tracker
	const getActualSpending = useCallback(
		(categoryRefs) => {
			if (!categoryRefs || categoryRefs.length === 0 || !lineItems.length) return 0;

			const { start, end } = getCurrentMonthBounds();
			const matchMap = {};
			categoryRefs.forEach((ref) => {
				const [business, category] = ref.split(":");
				if (!business || !category) return;
				const businessLower = business.toLowerCase();
				if (!matchMap[businessLower]) matchMap[businessLower] = new Set();
				const rawCats = getRawCategoriesForRef(ref);
				rawCats.forEach((cat) => matchMap[businessLower].add(cat.toLowerCase()));
			});

			return lineItems
				.filter((item) => {
					const itemDate = new Date(item.date);
					if (itemDate < start || itemDate > end) return false;
					const itemBusiness = (item.business || "").toLowerCase();
					const itemCategory = (item.category || "").toLowerCase();
					const businessCategories = matchMap[itemBusiness];
					return businessCategories && businessCategories.has(itemCategory);
				})
				.reduce((sum, item) => sum + Math.abs(item.transaction || 0), 0);
		},
		[lineItems, getCurrentMonthBounds],
	);

	// Calculate totals with large-ticket item handling
	const getCalculatedTotals = useCallback(
		(dateKey) => {
			let incomeRegular = 0;
			let incomeLargeTicket = 0;
			let expenseRegular = 0;
			let expenseLargeTicket = 0;

			Object.entries(groupedItems).forEach(([sectionName, sectionData]) => {
				if (sectionName === "Business") return;

				Object.values(sectionData.categories)
					.flat()
					.forEach((item) => {
						const forecastValue = getEffectiveItemValue(item, dateKey);
						if (item.is_large_ticket) {
							const liveData = getLiveItemData(item.id, dateKey, forecastValue);
							if (liveData.hasData) {
								const value = forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
								if (sectionData.isIncome) incomeLargeTicket += value;
								else expenseLargeTicket += value;
							} else {
								if (sectionData.isIncome) incomeRegular += forecastValue;
								else expenseRegular += forecastValue;
							}
						} else {
							if (sectionData.isIncome) incomeRegular += forecastValue;
							else expenseRegular += forecastValue;
						}
					});
			});

			const invoiceNet = getEffectiveGross(dateKey) - getBusinessExpenses(dateKey);
			const taxes = invoiceNet > 0 ? invoiceNet * taxRate : 0;
			const takehome = invoiceNet - taxes;

			const totalIncome = incomeRegular + incomeLargeTicket + takehome;
			const totalExpenses = expenseRegular + expenseLargeTicket;
			const net = totalIncome + totalExpenses;

			return {
				income: totalIncome,
				expenses: totalExpenses,
				net,
				incomeRegular,
				incomeFixed: incomeLargeTicket + takehome,
				expenseRegular,
				expenseFixed: expenseLargeTicket,
				regularNet: incomeRegular + expenseRegular,
				fixedNet: incomeLargeTicket + expenseLargeTicket + takehome,
			};
		},
		[groupedItems, getEffectiveGross, getEffectiveItemValue, getLiveItemData, getBusinessExpenses],
	);

	// Chart data - matches table Balance row logic exactly
	const chartData = useMemo(() => {
		if (!forecastData?.months) return [];
		let runningBalance = forecastData.startingBalance || 0;
		const monthProg = getMonthProgress();

		return forecastData.months.map((m) => {
			if (isMonthClosed(m.dateKey)) {
				const actualNetworth = networthByMonth[m.dateKey];
				if (actualNetworth !== undefined) {
					return { label: m.label, balance: actualNetworth, isActual: true, dateKey: m.dateKey };
				}
				return { label: m.label, balance: null, isActual: false, dateKey: m.dateKey };
			}

			const calculated = getCalculatedTotals(m.dateKey);
			const isCurrentMo = isCurrentMonth(m.dateKey);

			let monthNet;
			if (isCurrentMo) {
				const regularNet = calculated?.regularNet ?? 0;
				const fixedNet = calculated?.fixedNet ?? 0;
				monthNet = regularNet * (1 - monthProg) + fixedNet;
			} else {
				monthNet = calculated?.net ?? 0;
			}

			const safeNet = isNaN(monthNet) ? 0 : monthNet;
			runningBalance += safeNet;
			if (isNaN(runningBalance)) runningBalance = forecastData.startingBalance || 0;
			return { label: m.label, balance: runningBalance, isActual: false, dateKey: m.dateKey };
		});
	}, [forecastData, getCalculatedTotals, isMonthClosed, isCurrentMonth, networthByMonth, getMonthProgress]);

	// EOM Estimate: projected balance for the current month
	const eomEstimate = useMemo(() => {
		const now = new Date();
		const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
		const currentPoint = chartData.find((d) => d.dateKey === currentKey);
		if (currentPoint && currentPoint.balance !== null) return currentPoint.balance;
		const validPoints = chartData.filter((d) => d.balance !== null);
		return validPoints.length > 0 ? validPoints[validPoints.length - 1].balance : (forecastData?.startingBalance || 0);
	}, [chartData, forecastData?.startingBalance]);

	// Year-end projection
	const yearEndBalance = useMemo(() => {
		const fallback = forecastData?.startingBalance || 0;
		const now = new Date();
		const currentYear = now.getFullYear();
		const decemberKey = `${currentYear}-12`;
		const decPoint = chartData.find((d) => d.dateKey === decemberKey);
		if (decPoint && decPoint.balance !== null) return decPoint.balance;
		const validPoints = chartData.filter((d) => d.balance !== null);
		return validPoints.length > 0 ? validPoints[validPoints.length - 1].balance : fallback;
	}, [chartData, forecastData?.startingBalance]);

	// LineChart-ready data
	const lineChartData = useMemo(() => {
		return chartData.map((d) => ({
			label: d.label,
			value: d.balance,
			isActual: d.isActual,
			dateKey: d.dateKey,
		}));
	}, [chartData]);

	// Tracked budget items for current month tracker
	const getTrackedBudgetItems = useCallback(() => {
		if (!forecastData?.items) return [];

		const now = new Date();
		const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

		return forecastData.items
			.filter((item) => {
				let refs = item.live_category_ref;
				if (typeof refs === "string" && refs) {
					try {
						refs = JSON.parse(refs);
					} catch {
						refs = [];
					}
				}
				if (!refs || refs.length === 0) return false;
				return (item.monthlyValues[currentKey] || 0) !== 0;
			})
			.map((item) => {
				let refs = item.live_category_ref;
				if (typeof refs === "string") {
					try {
						refs = JSON.parse(refs);
					} catch {
						refs = [];
					}
				}

				const budget = Math.abs(item.monthlyValues[currentKey] || 0);
				const actual = getActualSpending(refs);
				const percentUsed = budget > 0 ? (actual / budget) * 100 : 0;
				const monthProgress = getMonthProgress() * 100;

				let status = "on-track";
				if (percentUsed > 100) status = "over";
				else if (percentUsed > monthProgress + 10) status = "ahead";

				return { ...item, categoryRefs: refs, budget, actual, percentUsed, status };
			});
	}, [forecastData, getActualSpending, getMonthProgress]);

	const getTrackerTotals = useCallback((items) => {
		const totalBudget = items.reduce((sum, item) => sum + item.budget, 0);
		const totalActual = items.reduce((sum, item) => sum + item.actual, 0);
		const percentUsed = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;
		const monthProgress = getMonthProgress() * 100;

		let status = "on-track";
		if (percentUsed > 100) status = "over";
		else if (percentUsed > monthProgress + 10) status = "ahead";

		return { budget: totalBudget, actual: totalActual, percentUsed, status };
	}, [getMonthProgress]);

	return {
		// Core data
		forecastData,
		loading,
		error,
		calculatedBalance,
		lineItems,

		// Grouped/processed data
		groupedItems,
		invoicesByMonth,
		historicalIncomeByMonth,
		historicalExpensesByMonth,
		networthByMonth,
		actualSpendingByMonth,
		liveActualsByItem,
		overdueAmount,

		// Key outputs
		eomEstimate,
		yearEndBalance,
		startingBalance: forecastData?.startingBalance || 0,
		chartData,
		lineChartData,

		// Helper functions
		getEffectiveGross,
		isUsingEstimate,
		getBusinessExpenses,
		getCalculatedTotals,
		isMonthClosed,
		isCurrentMonth,
		shouldShowActuals,
		getLiveItemData,
		getEffectiveItemValue,
		getActualSpending,
		getMonthProgress,
		getCurrentMonthBounds,
		getTrackedBudgetItems,
		getTrackerTotals,
	};
};

export default useBudgetForecast;
